"use strict";
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.AppHeader = AppHeader;
var jsx_runtime_1 = require("react/jsx-runtime");
// ================================================================
// v2/components/AppHeader/index.tsx
// 明るいナビバー (iOS 風 StatusBar + 戻る / タイトル / アクションアイコン)。
// EventDetail v2 を含むモバイル画面上部で横断利用する。
//
// スクロール追従 (Figma: EventDetail--myTicket に準拠):
//   - position: sticky / top: 0 で画面上部に固定する。
//   - トップ (scrolled=false): 背景透明 (背後のヒーロー / ページ背景を透過表示)。
//   - スクロール時 (scrolled=true): background.surface (半透明) + backdrop-blur +
//     下境界線に切り替え、下を流れるコンテンツをガラス越しに透過表示する。
//   scrolled はスクロール検知を持つ呼び出し側 (テンプレート) から渡す。
//
// 配色:
//   - タイトル文字: foreground.neutral.primary
//   - 戻るアイコン:  foreground.neutral.primary
//   - アクションアイコン: foreground.neutral.secondary
//   - 件数バッジ (badgeCount): 面 background.primary.default / 文字 white
// 色はすべて theme.palette.v2 (useV2Palette) から取得する。
// ================================================================
var material_1 = require("@mui/material");
var theme_1 = require("../../theme");
var tokens_1 = require("../../tokens");
var Icons_1 = require("../Icons");
/**
 * ヘッダーアイコンの大きさ (px)。Figma の EventDetail--myTicket は 24px 角の
 * アイコンを 44px 角のタップ領域に収めており、v1 の Header (IconButton の
 * size="medium") と同じ寸法になる。
 *
 * サイズをここで固定しているのは、utoniq-app のテーマが MuiIconButton の
 * sizeSmall に `svg { fontSize: 20 }` を当てており、size="small" のままだと
 * アイコンが 24px にならないため。テーマに依存せず Figma の値で描く。
 */
var ICON_SIZE = 24;
var ICON_BUTTON_SIZE = 44;
var ICON_BUTTON_SX = {
    width: ICON_BUTTON_SIZE,
    height: ICON_BUTTON_SIZE,
    flexShrink: 0,
    '& .MuiSvgIcon-root': { fontSize: "".concat(ICON_SIZE, "px") },
};
/**
 * セーフエリア (ノッチ) 分の帯。高さは env(safe-area-inset-top)。
 * Web では 0 (帯なし)、モバイルではノッチ分の余白として機能する。
 * 背景はヘッダー本体から継承する (透明 / スクロール時ガラス)。
 */
function StatusBar() {
    return ((0, jsx_runtime_1.jsx)(material_1.Box, { sx: {
            height: 'env(safe-area-inset-top, 0px)',
            flexShrink: 0,
        } }));
}
function AppHeader(_a) {
    var title = _a.title, onBack = _a.onBack, backLabel = _a.backLabel, _b = _a.backVariant, backVariant = _b === void 0 ? 'arrow' : _b, actions = _a.actions, _c = _a.scrolled, scrolled = _c === void 0 ? false : _c;
    var p = (0, theme_1.useV2Palette)();
    var actionItems = actions !== null && actions !== void 0 ? actions : [];
    // タイトルを中央に置いたまま左右のボタンにかぶらせないための内側余白。
    // 戻る 1 個とアクション n 個のうち、幅の広い側に合わせる。
    var titleInset = tokens_1.spacing.sm + ICON_BUTTON_SIZE * Math.max(1, actionItems.length);
    return ((0, jsx_runtime_1.jsxs)(material_1.Box, { sx: {
            position: 'sticky',
            top: 0,
            zIndex: function (theme) { return theme.zIndex.appBar; },
            // スクロール時のみガラス面。トップでは透明で背後を見せる。
            backgroundColor: scrolled ? p.background.surface : 'transparent',
            backdropFilter: scrolled ? 'blur(16px)' : 'none',
            WebkitBackdropFilter: scrolled ? 'blur(16px)' : 'none',
            borderBottom: "1px solid ".concat(scrolled ? p.border.neutral.default : 'transparent'),
            transition: 'background-color 0.2s ease, border-color 0.2s ease, backdrop-filter 0.2s ease',
            flexShrink: 0,
        }, children: [(0, jsx_runtime_1.jsx)(StatusBar, {}), (0, jsx_runtime_1.jsxs)(material_1.Stack, { direction: "row", alignItems: "center", sx: { position: 'relative', height: 56, px: "".concat(tokens_1.spacing.sm, "px") }, children: [(0, jsx_runtime_1.jsx)(material_1.IconButton, { onClick: onBack, "aria-label": backLabel, size: "medium", sx: __assign(__assign({}, ICON_BUTTON_SX), { color: p.foreground.neutral.primary }), children: backVariant === 'close' ? ((0, jsx_runtime_1.jsx)(Icons_1.XIcon, { sx: { fontSize: ICON_SIZE } })) : ((0, jsx_runtime_1.jsx)(Icons_1.ArrowLeftIcon, { sx: { fontSize: ICON_SIZE } })) }), (0, jsx_runtime_1.jsx)(material_1.Box, { sx: { flexGrow: 1 } }), (0, jsx_runtime_1.jsx)(material_1.Typography, { sx: {
                            position: 'absolute',
                            left: 0,
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            px: "".concat(titleInset, "px"),
                            // ボタンのタップ判定を絶対配置のタイトルで塞がない
                            pointerEvents: 'none',
                            fontSize: tokens_1.fontSize.base,
                            fontWeight: tokens_1.fontWeight.semiBold,
                            lineHeight: "".concat(tokens_1.lineHeight.base, "px"),
                            color: p.foreground.neutral.primary,
                            textAlign: 'center',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }, children: title }), (0, jsx_runtime_1.jsx)(material_1.Stack, { direction: "row", spacing: 0, children: actionItems.map(function (action, i) { return ((0, jsx_runtime_1.jsx)(material_1.IconButton, { onClick: action.onClick, "aria-label": action.label, size: "medium", sx: __assign(__assign({}, ICON_BUTTON_SX), { color: p.foreground.neutral.secondary }), children: action.badgeCount ? ((0, jsx_runtime_1.jsx)(material_1.Badge, { badgeContent: action.badgeCount, sx: {
                                    '& .MuiBadge-badge': {
                                        // 既定の translate(50%, -50%) は IconButton の外へ
                                        // 張り出すため無効化し、ボタン枠の内側に収める
                                        // (最右のアクションでもヘッダー端に干渉しない)。
                                        transform: 'none',
                                        transformOrigin: 'initial',
                                        top: -2,
                                        right: -2,
                                        // 3桁 ("99+") でも欠けないよう幅は固定しない
                                        minWidth: 16,
                                        height: 16,
                                        padding: '0 4px',
                                        fontSize: tokens_1.fontSize['2xs'],
                                        fontWeight: tokens_1.fontWeight.semiBold,
                                        lineHeight: "".concat(tokens_1.lineHeight['2xs'], "px"),
                                        // 面はアプリのブランドカラー (withV2BrandColors) に
                                        // 差し替わるため、文字色は面の明度から決める
                                        color: (0, theme_1.pickFilledInk)(p.background.primary.default, p),
                                        backgroundColor: p.background.primary.default,
                                    },
                                }, children: action.icon })) : (action.icon) }, i)); }) })] })] }));
}
